import React, { useId } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { Input, Label, RadioGroup } from '@/fluent-adapter';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';
import {
  PeopleCommunity24Regular as AgencyIcon,
  ContactCard24Regular as PersonIcon,
  Mail24Regular as MailIcon,
} from '@fluentui/react-icons';
import { PhoneInput } from '@/components/PhoneInput';
import type { AgentInformationForm, TypeOfRequestForm } from '../../store/types';
import { FIELD_LIMITS } from '../../constants/fieldLimits';

interface AgentInformationStepProps {
  formData: AgentInformationForm;
  onFormChange: (data: Partial<AgentInformationForm>) => void;
  typeOfRequestData?: TypeOfRequestForm;
  onTypeOfRequestChange?: (data: Partial<TypeOfRequestForm>) => void;
  officialEmail?: string;
  errors?: Record<string, string>;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXXL,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalL,
    flexWrap: 'wrap',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
    },
  },
  rowItem: {
    flex: '1 1 100%',
    minWidth: '0',
    '@media (min-width: 768px)': {
      flex: '1 1 280px',
      minWidth: '240px',
    },
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  helper: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },
  iconLabel: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  readOnlyField: {
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'not-allowed',
  },
  required: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

export const AgentInformationStep: React.FC<AgentInformationStepProps> = ({
  formData,
  onFormChange,
  officialEmail,
  errors = {},
}) => {
  const styles = useStyles();
  const receivingAgentLabelId = useId();
  const receivingAgentDescId = useId();

  return (
    <div className={styles.root}>
      <Card>
        <CardHeader>
          <CardTitle className={styles.sectionTitle}>Submitting Agent</CardTitle>
          <CardDescription>
            Provide the contact information for the law enforcement agent submitting this request
          </CardDescription>
        </CardHeader>
        <CardContent className={styles.cardContent}>
          <div className={styles.fieldGroup}>
            <Label className={styles.iconLabel} htmlFor="agencyName">
              <AgencyIcon /> Law Enforcement Agency Name<RequiredIndicator />
            </Label>
            <Input
              id="agencyName"
              value={formData.agencyName}
              onChange={(e) => onFormChange({ agencyName: e.target.value })}
              placeholder="Agency Name"
              maxLength={FIELD_LIMITS.AGENCY_NAME}
            />
            {errors.agencyName && <span className={styles.errorText}>{errors.agencyName}</span>}
          </div>

          <div className={styles.row}>
            <div className={`${styles.fieldGroup} ${styles.rowItem}`}>
              <Label className={styles.iconLabel} htmlFor="agentFirstName">
                <PersonIcon /> First Name<RequiredIndicator />
              </Label>
              <Input
                id="agentFirstName"
                value={formData.agentFirstName}
                onChange={(e) => onFormChange({ agentFirstName: e.target.value })}
                placeholder="First"
                maxLength={FIELD_LIMITS.PERSON_NAME}
              />
              {errors.agentFirstName && <span className={styles.errorText}>{errors.agentFirstName}</span>}
            </div>
            <div className={`${styles.fieldGroup} ${styles.rowItem}`}>
              <Label className={styles.iconLabel} htmlFor="agentLastName">
                <PersonIcon /> Last Name<RequiredIndicator />
              </Label>
              <Input
                id="agentLastName"
                value={formData.agentLastName}
                onChange={(e) => onFormChange({ agentLastName: e.target.value })}
                placeholder="Last"
                maxLength={FIELD_LIMITS.PERSON_NAME}
              />
              {errors.agentLastName && <span className={styles.errorText}>{errors.agentLastName}</span>}
            </div>
          </div>

          <div className={styles.row}>
            <div className={`${styles.fieldGroup} ${styles.rowItem}`}>
              <Label className={styles.iconLabel} htmlFor="agentEmail">
                <MailIcon /> Official Email Address<RequiredIndicator />
              </Label>
              {officialEmail ? (
                <>
                  <Input
                    id="agentEmail"
                    type="email"
                    value={officialEmail}
                    disabled
                    className={styles.readOnlyField}
                  />
                  <span className={styles.helper}>This email is pre-populated from your account and cannot be changed.</span>
                </>
              ) : (
                <>
                  <Input
                    id="agentEmail"
                    type="email"
                    value={formData.agentEmail}
                    onChange={(e) => onFormChange({ agentEmail: e.target.value })}
                    placeholder="Email"
                    maxLength={FIELD_LIMITS.EMAIL}
                  />
                  {errors.agentEmail && <span className={styles.errorText}>{errors.agentEmail}</span>}
                </>
              )}
            </div>
            <div className={`${styles.fieldGroup} ${styles.rowItem}`}>
              <PhoneInput
                id="agentPhoneNumber"
                label="Phone Number"
                value={formData.agentPhoneNumber}
                onChange={(value) => onFormChange({ agentPhoneNumber: value })}
                error={errors.agentPhoneNumber}
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle id={receivingAgentLabelId} className={styles.sectionTitle}>
            Receiving Agent
          </CardTitle>
          <CardDescription id={receivingAgentDescId}>
            Do you want notifications sent to a different agent? If yes, note that only the receiving agent will receive any information related to this request.
          </CardDescription>
        </CardHeader>
        <CardContent className={styles.cardContent}>
          <RadioGroup
            aria-labelledby={receivingAgentLabelId}
            aria-describedby={receivingAgentDescId}
            value={formData.hasReceivingAgent ? 'true' : 'false'}
            onValueChange={(value) => {
              const hasAgent = value === 'true';
              if (!hasAgent) {
                onFormChange({
                  hasReceivingAgent: hasAgent,
                  receivingAgentEmail: ''
                });
              } else {
                onFormChange({ hasReceivingAgent: hasAgent });
              }
            }}
            items={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
          />

          {formData.hasReceivingAgent && (
            <div className={styles.fieldGroup}>
              <Label className={styles.iconLabel} htmlFor="receivingAgentEmail">
                <MailIcon /> Receiving Agent Email Address
              </Label>
              <Input
                id="receivingAgentEmail"
                type="email"
                value={formData.receivingAgentEmail}
                onChange={(e) => onFormChange({ receivingAgentEmail: e.target.value })}
                placeholder="Enter receiving agent's government email address"
                maxLength={FIELD_LIMITS.RECEIVING_AGENT_EMAIL}
              />
              <span className={styles.muted}>Maximum {FIELD_LIMITS.RECEIVING_AGENT_EMAIL} characters</span>
              {errors.receivingAgentEmail && <span className={styles.errorText}>{errors.receivingAgentEmail}</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
